import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * MESAİ ÖN İZNİ UÇLARI (9).
 *
 * SIRA ÖNEMLİ: önce ön izin, sonra mesai. Tersine çevrilirse "izinsiz mesai
 * oranı" göstergesi anlamını yitirir — o gösterge pilotun temel ölçütü ve
 * fazla çalışmanın istisna mı kural mı olduğunu söylüyor.
 *
 * YETKİ ÜÇ SEVİYEDE:
 *   POST /on-kontrol     MESAI_ON_IZIN_TALEP | MESAI_ON_IZIN_ONAY
 *   POST /               MESAI_ON_IZIN_TALEP                     ← TEK
 *   GET  / · /:id        + RAPOR_GORUNTULE
 *   GET  /onay-bekleyenler · PATCH onayla|reddet   MESAI_ON_IZIN_ONAY  ← TEK
 *   GET  /rapor/on-izinsiz-oran   + DENETIM_EKRANI
 *
 * TALEP EDEN İLE ONAYLAYAN AYRI (4857/41): fazla çalışma onayı işverenin,
 * talep birim yöneticisinin. Şef talep eder, müdür onaylar.
 */

export type OnIzinOzeti = Semalar['OnIzinOzetiDto'];
export type OnIzinOnKontrolSonucu = Semalar['OnIzinOnKontrolSonucuDto'];
export type OnIzinTalepYaniti = Semalar['OnIzinTalepYanitDto'];
export type OnIzinsizOranSatiri = Semalar['OnIzinsizOranSatiriDto'];
export type KotasiDolan = Semalar['KotasiDolanDto'];

export type OnIzinDurumu = OnIzinOzeti['durum'];
export type OnIzinDayanagi = OnIzinOzeti['dayanak'];

export const DURUM_ETIKETI: Record<OnIzinDurumu, string> = {
  talep: 'Talep',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
  iptal: 'İptal',
};

/**
 * DAYANAK KAPALI LİSTE.
 *
 * Fazla çalışma dayanaksız yapılamaz (4857/41). Serbest metin olsaydı
 * "acil iş" yazıp geçilir ve denetimde hangi hükme dayandığı bilinemezdi.
 */
export const DAYANAK_ETIKETI: Record<OnIzinDayanagi, string> = {
  isci_onayi: 'İşçinin yazılı onayı',
  tis_hukmu: 'TİS hükmü',
  // 4857/42 ve 43 — zorunlu neden ve olağanüstü hâl aynı kovada; ikisi de
  // işçi onayı GEREKTİRMEYEN istisnalar.
  zorunlu_neden: 'Zorunlu neden (4857/42-43)',
};

export interface OnIzinSorgusu {
  birimId?: Kimlik;
  durum?: OnIzinDurumu;
  baslangic?: string;
  bitis?: string;
}

export interface OnIzinGirdisi {
  birimId: Kimlik;
  baslangicTarihi: string;
  bitisTarihi: string;
  /** METİN — ondalık saat (`2.50`). Sayıya çevrilirse yuvarlama kaybı olur. */
  tahminiSaat: string;
  tahminiKisi: number;
  gerekce: string;
  dayanak: OnIzinDayanagi;
  fazlaCalismaOnayiVar?: boolean;
  aciklama?: string;
}

/* ------------------------------------------------------------------ */

/**
 * Talep açılmadan ÖNCE maliyet ve kota etkisi.
 *
 * Ayrı uç olması bilinçli: yönetici tahmini tutarı ve 270 saat kotasına
 * etkisini talebi kaydetmeden görmeli. Kaydettikten sonra gösterilseydi
 * geri alma gerekir, geri alma da denetim izine yazılırdı.
 */
export function onIzinOnKontrol(girdi: OnIzinGirdisi): Promise<OnIzinOnKontrolSonucu> {
  return istek<OnIzinOnKontrolSonucu>('/v1/mesai-on-izin/on-kontrol', {
    yontem: 'POST',
    govde: girdi,
  });
}

export function onIzinTalepEt(girdi: OnIzinGirdisi): Promise<OnIzinTalepYaniti> {
  return istek<OnIzinTalepYaniti>('/v1/mesai-on-izin', { yontem: 'POST', govde: girdi });
}

export function onIzinListesi(sorgu: OnIzinSorgusu = {}): Promise<OnIzinOzeti[]> {
  return istek<OnIzinOzeti[]>('/v1/mesai-on-izin', { sorgu: { ...sorgu } });
}

export function onIzinOnayBekleyenler(): Promise<OnIzinOzeti[]> {
  return istek<OnIzinOzeti[]>('/v1/mesai-on-izin/onay-bekleyenler');
}

export function onIzinAyrintisi(id: Kimlik): Promise<OnIzinOzeti> {
  return istek<OnIzinOzeti>(`/v1/mesai-on-izin/${id}`);
}

/** `aciklama` İSTEĞE BAĞLI (`OnayDto`) — reddin aksine. */
export function onIzinOnayla(id: Kimlik, aciklama?: string): Promise<void> {
  return istek<void>(`/v1/mesai-on-izin/${id}/onayla`, {
    yontem: 'PATCH',
    govde: { aciklama },
  });
}

/** Gerekçe ZORUNLU: talep eden neden reddedildiğini bilmeli. */
export function onIzinReddet(id: Kimlik, gerekce: string): Promise<void> {
  return istek<void>(`/v1/mesai-on-izin/${id}/reddet`, {
    yontem: 'PATCH',
    govde: { gerekce },
  });
}

/**
 * ÖN İZİNSİZ GERÇEKLEŞEN MESAİ ORANI — pilotun temel göstergesi.
 *
 * Yüksekse ya süreç işlemiyor ya mesai plansız yapılıyor. İkisi de yönetim
 * sorunu ve rakamla konuşulmadan tartışılamaz.
 */
export function onIzinsizOran(baslangic: string, bitis: string): Promise<OnIzinsizOranSatiri[]> {
  return istek<OnIzinsizOranSatiri[]>('/v1/mesai-on-izin/rapor/on-izinsiz-oran', {
    sorgu: { baslangic, bitis },
  });
}
